import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const SHOTS = [
  ["/app-shots/01-live-shade-status.jpeg", "Live shade status", "Which side stays shaded, right now"],
  ["/app-shots/02-parking-heading.jpeg", "Set your heading", "Dial in the way your car faces"],
  ["/app-shots/03-park-spot-recommendations.jpeg", "Where to park", "Headings ranked by shade time"],
  ["/app-shots/04-best-shade-window.jpeg", "Best shade window", "See how long the shade lasts"],
  ["/app-shots/06-favorite-spots.jpeg", "Favourite spots", "Save and recall your go-to bays"],
  ["/app-shots/07-manual-location.jpeg", "Manual location", "Type an address or drop a pin"],
  ["/app-shots/08-sunlight-mode.jpeg", "Sunlight Mode", "High-contrast for bright glare"],
  ["/app-shots/09-privacy-offline.jpeg", "Privacy, offline", "Your data stays on your device"],
  ["/app-shots/10-growth-vision.jpeg", "Settings & Growth", "Tune the app, glimpse what's next"],
];

export default function AppGallery() {
  return (
    <section
      id="inside-the-app"
      data-testid="app-gallery-section"
      className="border-b border-[var(--sp-line)] bg-[var(--sp-surface)]"
    >
      <div className="mx-auto max-w-[1500px] px-5 py-20 sm:px-8 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="mb-4 flex items-center gap-3 font-mono2 text-xs uppercase tracking-[0.28em] text-[var(--sun)]"
            >
              <span className="h-px w-10 bg-[var(--sun)]" />
              Inside The App // 04
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-2xl font-display text-2xl font-extrabold uppercase leading-none tracking-tight sm:text-4xl lg:text-5xl"
            >
              The whole tour, screen by screen
            </motion.h2>
          </div>
          <p className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.14em] text-[var(--sp-faint)]">
            Scroll to explore <ArrowRight className="h-3.5 w-3.5" />
          </p>
        </div>

        <div
          data-testid="app-gallery-scroller"
          className="mt-14 flex snap-x snap-mandatory gap-8 overflow-x-auto pb-8"
        >
          {SHOTS.map(([src, title, sub], i) => (
            <motion.figure
              key={src}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`app-gallery-shot-${i}`}
              className="w-[220px] shrink-0 snap-start sm:w-[248px]"
            >
              <span className="app-screen w-full">
                <img
                  src={src}
                  alt={`ShadyParking app screen: ${title} — ${sub}`}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-contain"
                />
              </span>
              <figcaption className="mt-6">
                <p className="font-display text-base font-bold uppercase tracking-tight">{title}</p>
                <p className="mt-1 font-mono2 text-[10px] uppercase leading-relaxed tracking-[0.1em] text-[var(--sp-muted)]">
                  {sub}
                </p>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
